import axios from 'axios';
import _ from 'underscore';
import { SUPPORTED_FILE_SIZE } from '@shoflo/polymer/constants/MediaTypes';

import FileTypes from 'constants/FileTypes';
import ProfilePhotoMimeTypes from 'constants/ProfilePhotoMimeTypes';
import { UploadStatusMessages } from 'constants/EventDocUploadStatuses';
const { InvalidFile, FileTooLarge, UnsupportedFileType } = UploadStatusMessages;

const invalidMagicNumbers = [
  '4D 5A', //exe
  '5A 4D' //exe
].map(str => str.replace(/\s+/g, ''));

async function getMagicNumber(file) {
  const blob = file.slice(0, 12);

  return await new Promise((res, rej) => {
    const fileReader = new FileReader();

    fileReader.onloadend = function (evt) {
      if (evt.target.readyState === FileReader.DONE) {
        const uint = new Uint8Array(evt.target.result);
        const bytes = [];
        uint.forEach(byte => {
          bytes.push(('0' + (byte & 0xff).toString(16)).slice(-2));
        });
        const hex = bytes.join('').toUpperCase();

        res(hex);
      }
    };
    fileReader.onerror = function () {
      rej('error reading file');
    };
    fileReader.readAsArrayBuffer(blob);
  });
}

const isInvalidMagicNumber = hex => {
  let isInvalid = false;
  invalidMagicNumbers.forEach(num => {
    if (hex.slice(0, num.length).includes(num)) isInvalid = true;
  });
  return isInvalid;
};

export default (path, params, file, fileFormFieldName) => {
  const keyVals = {
    params: JSON.stringify(params)
  };

  keyVals[fileFormFieldName || 'file'] = file;

  return uploadForm(path, keyVals);
};

/**
 * @param apiClient
 * @param {string} teamId
 * @param {string} eventId
 * @param {string} file
 * @param {string} parentFolderId
 * @param {boolean} isStudioMedia If set true it'll upload doc to studio media folder
 */
export const uploadEventDoc = async (
  apiClient,
  teamId,
  eventId,
  file,
  parentFolderId,
  isStudioMedia = false
) => {
  const { name, size } = file;
  let magicNumber;

  try {
    magicNumber = await getMagicNumber(file);
  } catch (e) {
    throw new Error(InvalidFile);
  }

  const fileTypes = Object.keys(FileTypes);
  const fileExtension = name.split('.').pop().toLowerCase();
  const validFileExtension = fileTypes.find(ext => ext === fileExtension);

  if (isInvalidMagicNumber(magicNumber)) {
    throw new Error(InvalidFile);
  }

  if (!validFileExtension) {
    throw new Error(UnsupportedFileType);
  }

  if (size > SUPPORTED_FILE_SIZE) {
    throw new Error(FileTooLarge);
  }

  const contentType = FileTypes[fileExtension][0];

  const asyncPayload = { name, isStudioMedia }; // these 2 props are required

  if (parentFolderId) {
    asyncPayload.parentFolderId = parentFolderId;
  }

  return await uploadFormAsync(
    apiClient,
    teamId,
    eventId,
    file,
    contentType,
    asyncPayload
  );
};

export const uploadForm = async (path, keyVals, config = {}) => {
  const formData = new FormData();

  _.each(keyVals, function (val, key) {
    formData.append(key, val);
  });

  const res = await axios.post(path, formData, config);

  return res.data;
};

/**
 * Will return file in binary format
 *
 * @param file
 */
export function readFileAsBinary(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const binaryData = reader.result;
      resolve(binaryData);
    };

    reader.onerror = () => {
      reject(reader.error || new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

export const uploadFormAsync = async (
  apiClient,
  teamId,
  eventId,
  file,
  contentType,
  payload
) => {
  const response = await apiClient.eventDoc.createDocument(
    teamId,
    eventId,
    payload
  );

  const { preSignedUploadUrl, preSignedUploadHeaders } = response;

  const binaryData = await readFileAsBinary(file);

  const uploadHeaders = preSignedUploadHeaders?.['x-amz-tagging']?.join(',');

  await axios.put(preSignedUploadUrl, binaryData, {
    headers: {
      'Content-Type': contentType,
      ...(uploadHeaders && { 'x-amz-tagging': uploadHeaders })
    }
  });
};

export const isValidImageMimeType = mimeType => {
  return ProfilePhotoMimeTypes.includes(mimeType);
};
